import React, { useEffect, useState } from "react";
import styles from "../../css/shared/CustomSelect.module.scss";
import sprite from "../../assets/icons/carets-arrows.svg";
import { PropTypes } from "prop-types";
import { isEmptyArray, isEmptyVal } from "../../helpers/utils_types";
import CustomSelectDropdown from "./CustomSelectDropdown";

// REQUIREMENTS:
// - Replicates <MultiSelect/> functionality, but for single select

const advancedSearch = (val, options) => {
	val = val?.toLowerCase();

	return options.filter((option) => {
		if (
			option.toLowerCase().startsWith(val) ||
			option.toLowerCase().includes(val)
		) {
			// matches
			return option;
		} else {
			return null;
		}
	});
};

// updated to support 'disableMultiSelect'
const getPlaceholder = (
	placeholder,
	selections = [],
	options = [],
	disableMultiSelect = false
) => {
	if (disableMultiSelect) {
		return !isEmptyArray(selections)
			? `${selections?.[0] ?? ""}`
			: `${!isEmptyVal(placeholder) ? placeholder : ""}`;
	}

	if (!placeholder) {
		return `${selections?.length ?? 0} of ${options?.length} selected`;
	} else {
		return placeholder;
	}
};

const CustomSelect = ({
	placeholder,
	handleCustomSelect,
	initialSelections = [],
	disabledOptions = [],
	options = [],
	disableMultiSelect = false,
	customOption = null,
	customStyles = {},
	dropdownStyles = {},
}) => {
	const [selections, setSelections] = useState([...initialSelections]);
	const [showOptions, setShowOptions] = useState(false);
	const [clonedOptions, setClonedOptions] = useState([...options]);
	const [searchVal, setSearchVal] = useState("");

	const handleSelect = (option) => {
		// only handles single select
		if (disableMultiSelect) return setSelections([option]);

		if (selections.includes(option)) {
			return setSelections([...selections.filter((x) => x !== option)]);
		}
		return setSelections([...selections, option]);
	};

	const handleSelectAll = () => {
		if (disableMultiSelect) return;

		if (selections.length === options.length) {
			return setSelections([]);
		}
		return setSelections([...options]);
	};

	const handleSearch = (e) => {
		const { value } = e.target;
		setSearchVal(value);
		return searchOptions(value);
	};

	const searchOptions = (val) => {
		if (isEmptyVal(val)) {
			return setClonedOptions([...options]);
		}
		return setClonedOptions([...advancedSearch(val, options)]);
	};

	const clearSelections = () => {
		setSelections([]);
		handleCustomSelect([]);
	};

	const saveSelections = () => {
		setShowOptions(false);
	};

	const clearSearch = () => {
		setSearchVal("");
		searchOptions("");
	};

	// used to send "selections" to the parent via "handleCustomSelect"
	useEffect(() => {
		let isMounted = true;
		if (!isMounted) {
			return;
		}
		handleCustomSelect(selections);
		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selections]);

	return (
		<div className={styles.CustomSelect} style={customStyles}>
			<div
				className={styles.CustomSelect_input}
				onClick={showOptions ? null : () => setShowOptions(true)}
				style={customStyles}
			>
				<span className={styles.CustomSelect_input_placeholder}>
					{getPlaceholder(placeholder, selections, options, disableMultiSelect)}
				</span>
				<svg
					className={styles.CustomSelect_input_downCaret}
					onClick={() => setShowOptions(true)}
				>
					<use xlinkHref={`${sprite}#icon-caret-down`}></use>
				</svg>
			</div>
			{showOptions && (
				<CustomSelectDropdown
					customOption={customOption}
					searchVal={searchVal}
					disabledOptions={disabledOptions}
					options={clonedOptions}
					selections={selections}
					isAllSelected={selections.length === options.length}
					handleSelect={handleSelect}
					handleSelectAll={handleSelectAll}
					handleSearch={handleSearch}
					saveSelections={saveSelections}
					clearSelections={clearSelections}
					clearSearch={clearSearch}
					closeMenuOptions={() => setShowOptions(false)}
					dropdownStyles={dropdownStyles}
					disableMultiSelect={disableMultiSelect}
				/>
			)}
		</div>
	);
};

export default CustomSelect;

CustomSelect.defaultProps = {
	options: [],
};
CustomSelect.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	).isRequired,
	handleCustomSelect: PropTypes.func.isRequired, // handler from parent component; keeps "selections" in-sync
};
